// Agency Presentation Profiles — DISPLAY ONLY.
//
// This layer relabels + reorders sidebar items and picks a default landing
// page per organization type. It does NOT grant or revoke access — feature
// visibility is still governed exclusively by entitlements.ts / useFeatureFlags.
// Engines, API routes, and page components are untouched ("zero core changes").

export interface AgencyProfile {
  /** Brand text shown in the sidebar header. */
  brand: string;
  /** Default page id shown right after login (must be an always-on or entitled page). */
  landing: string;
  /** pageId -> agency-facing label (overrides the built-in sidebar label). */
  labels: Record<string, string>;
  /** Optional sidebar ordering by pageId (unlisted items keep their default order after these). */
  order?: string[];
}

export const AGENCY_PROFILES: Record<string, AgencyProfile> = {
  news_media: {
    brand: 'Media Shield',
    landing: 'home',
    labels: {
      home: 'Overview',
      topics: 'Narrative Radar',
      narrative_map: 'Global Story Map',
      intelligence_center: 'Investigation Hub',
      simulation: 'Virality Simulator',
      alerts: 'Alerts & Briefs',
      analytics: 'Reports & Exports',
    },
  },
  government: {
    brand: 'Government & Public Sector',
    landing: 'global_monitor',
    labels: {
      home: 'Command Overview',
      global_monitor: 'Global Command Center',
      narrative_map: 'Narrative Map',
      intelligence_center: 'Intelligence Center',
      simulation: 'Scenario Simulator',
      alerts: 'Alerts & Briefs',
    },
  },
  financial_services: {
    brand: 'Business Intelligence',
    landing: 'business_intelligence',
    labels: {
      home: 'Overview',
      intelligence_center: 'Threat Intelligence',
      simulation: 'Market Impact Simulator',
      analytics: 'Reports & Exports',
    },
  },
  enterprise_security: {
    brand: 'Enterprise Security',
    landing: 'intelligence_center',
    labels: {
      intelligence_center: 'Threat Intelligence',
      analytics: 'Reports & Exports',
    },
  },
  critical_infrastructure: {
    brand: 'Critical Infrastructure',
    landing: 'global_monitor',
    labels: {
      global_monitor: 'Operations Command',
      intelligence_center: 'Threat Intelligence',
      narrative_map: 'Situation Map',
      alerts: 'Alerts & Briefs',
    },
  },
  other: {
    brand: 'CrisisShield',
    landing: 'home',
    labels: {},
  },
};

const DEFAULT_PROFILE = AGENCY_PROFILES.other;

/** Resolve the presentation profile for an org type, falling back to the neutral default. */
export function getAgencyProfile(orgType: string | null | undefined): AgencyProfile {
  if (!orgType) return DEFAULT_PROFILE;
  return AGENCY_PROFILES[orgType.toLowerCase()] || DEFAULT_PROFILE;
}

/** Agency-facing label for a page, falling back to the built-in sidebar label. */
export function getAgencyLabel(
  orgType: string | null | undefined,
  pageId: string,
  fallback: string,
): string {
  return getAgencyProfile(orgType).labels[pageId] || fallback;
}
